import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/gestionale/integrations/supabase/client";
import type { Tables, TablesInsert } from "@/gestionale/integrations/supabase/types";
import type { Database } from "@/gestionale/integrations/supabase/types";
import { motion } from "framer-motion";
import { Plus, Pencil, Trash2, Search, Users, ShieldCheck, ShieldX, CheckCircle2, XCircle, Minus } from "lucide-react";
import { Button } from "@/gestionale/components/ui/button";
import { Input } from "@/gestionale/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/gestionale/components/ui/select";
import { Badge } from "@/gestionale/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/gestionale/components/ui/table";
import { toast } from "sonner";
import { cn } from "@/gestionale/lib/utils";
import PersonaDialog from "@/gestionale/components/persone/PersonaDialog";
import DeletePersonaDialog from "@/gestionale/components/persone/DeletePersonaDialog";
import PersonaDetailSheet from "@/gestionale/components/persone/PersonaDetailSheet";

type Persona = Tables<"persone">;
type TipoRuolo = Database["public"]["Enums"]["tipo_ruolo"];

const RUOLO_COLORS: Record<TipoRuolo, string> = {
  Dirigente: "bg-primary/15 text-primary border-primary/30",
  Socio: "bg-blue-500/15 text-blue-700 border-blue-500/30",
  Abbonato: "bg-amber-500/15 text-amber-700 border-amber-500/30",
  Atleta: "bg-green-500/15 text-green-700 border-green-500/30",
  Giocatore: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
  Allenatore: "bg-purple-500/15 text-purple-700 border-purple-500/30",
  Istruttore: "bg-cyan-500/15 text-cyan-700 border-cyan-500/30",
  Genitore: "bg-rose-500/15 text-rose-700 border-rose-500/30",
};

// Tavolozza di colori per i corsi/discipline. Ogni corso riceve un colore
// stabile calcolato dal proprio nome, così due badge dello stesso corso
// sono sempre dello stesso colore, e corsi diversi sono facilmente
// distinguibili a colpo d'occhio, senza dover mantenere una lista manuale.
const CORSO_COLOR_PALETTE = [
  "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
  "bg-blue-500/15 text-blue-700 border-blue-500/30",
  "bg-purple-500/15 text-purple-700 border-purple-500/30",
  "bg-amber-500/15 text-amber-700 border-amber-500/30",
  "bg-pink-500/15 text-pink-700 border-pink-500/30",
  "bg-cyan-500/15 text-cyan-700 border-cyan-500/30",
  "bg-orange-500/15 text-orange-700 border-orange-500/30",
  "bg-indigo-500/15 text-indigo-700 border-indigo-500/30",
  "bg-teal-500/15 text-teal-700 border-teal-500/30",
  "bg-rose-500/15 text-rose-700 border-rose-500/30",
  "bg-lime-500/15 text-lime-700 border-lime-500/30",
  "bg-fuchsia-500/15 text-fuchsia-700 border-fuchsia-500/30",
];

function getCorsoColorClass(nomeCorso: string): string {
  let hash = 0;
  for (let i = 0; i < nomeCorso.length; i++) {
    hash = nomeCorso.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % CORSO_COLOR_PALETTE.length;
  return CORSO_COLOR_PALETTE[index];
}

export default function Persone() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPersona, setEditingPersona] = useState<Persona | null>(null);
  const [deletingPersona, setDeletingPersona] = useState<Persona | null>(null);
  const [detailPersona, setDetailPersona] = useState<Persona | null>(null);

  const { data: persone = [], isLoading } = useQuery({
    queryKey: ["persone"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("persone")
        .select("*")
        .order("cognome", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const { data: ruoliMap = {} } = useQuery({
    queryKey: ["ruoli"],
    queryFn: async () => {
      const { data, error } = await supabase.from("ruoli").select("*");
      if (error) throw error;
      const map: Record<string, TipoRuolo[]> = {};
      for (const r of data) {
        if (!map[r.persona_id]) map[r.persona_id] = [];
        map[r.persona_id].push(r.tipo_ruolo);
      }
      return map;
    },
  });

  const { data: abbonamenti = [] } = useQuery({
    queryKey: ["abbonamenti-lista"],
    queryFn: async () => {
      const { data, error } = await supabase.from("abbonamenti").select("persona_id, stato_pagamento, stagione, corso");
      if (error) throw error;
      return data;
    },
  });

  const { data: corsi = [] } = useQuery({
    queryKey: ["corsi-attivi-persone"],
    queryFn: async () => {
      const { data, error } = await supabase.from("corsi").select("id, nome").eq("attivo", true).order("nome");
      if (error) throw error;
      return data;
    },
  });

  const { data: istruttoreCorsi = [] } = useQuery({
    queryKey: ["istruttore-corsi"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("istruttore_corsi")
        .select("persona_id, corso_id");
      if (error) throw error;
      return data;
    },
  });

  const { data: personaCorsi = [] } = useQuery({
    queryKey: ["persona-corsi"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("persona_corsi")
        .select("persona_id, corso_id");
      if (error) throw error;
      return data;
    },
  });

  const [filtroCorso, setFiltroCorso] = useState<string>("tutti");
  const [filtroRuolo, setFiltroRuolo] = useState<string>("tutti");

  const personeIdsPerCorso = useMemo(() => {
    if (filtroCorso === "tutti") return null;
    const corso = corsi.find((c) => c.nome === filtroCorso);
    if (!corso) return new Set<string>();
    const ids = new Set<string>();
    personaCorsi.filter((pc) => pc.corso_id === corso.id).forEach((pc) => ids.add(pc.persona_id));
    istruttoreCorsi.filter((ic) => ic.corso_id === corso.id).forEach((ic) => ids.add(ic.persona_id));
    return ids;
  }, [corsi, personaCorsi, istruttoreCorsi, filtroCorso]);

  const abbonamentiMap = useMemo(() => {
    const map: Record<string, { attivo: boolean }> = {};
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const startYear = currentMonth >= 8 ? currentYear : currentYear - 1;
    const currentStagione = `${startYear}/${startYear + 1}`;

    for (const a of abbonamenti) {
      const isCurrentSeason = a.stagione === currentStagione;
      if (isCurrentSeason) {
        map[a.persona_id] = { attivo: true };
      } else if (!map[a.persona_id]) {
        map[a.persona_id] = { attivo: false };
      }
    }
    return map;
  }, [abbonamenti]);

  const istruttoreCorsiMap = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const ic of istruttoreCorsi) {
      const corso = corsi.find((c) => c.id === ic.corso_id);
      if (!corso) continue;
      if (!map[ic.persona_id]) map[ic.persona_id] = [];
      map[ic.persona_id].push(corso.nome);
    }
    return map;
  }, [istruttoreCorsi, corsi]);

  const atletaCorsiMap = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const pc of personaCorsi) {
      const corso = corsi.find((c) => c.id === pc.corso_id);
      if (!corso) continue;
      if (!map[pc.persona_id]) map[pc.persona_id] = [];
      map[pc.persona_id].push(corso.nome);
    }
    return map;
  }, [personaCorsi, corsi]);

  const upsertMutation = useMutation({
    mutationFn: async ({ persona, ruoli }: { persona: TablesInsert<"persone"> & { id?: string }; ruoli: TipoRuolo[] }) => {
      let personaId = persona.id;

      if (personaId) {
        const { error } = await supabase.from("persone").update(persona).eq("id", personaId);
        if (error) throw error;
      } else {
        const { data, error } = await supabase.from("persone").insert(persona).select("id").single();
        if (error) throw error;
        personaId = data.id;
      }

      await supabase.from("ruoli").delete().eq("persona_id", personaId);
      if (ruoli.length > 0) {
        const { error } = await supabase.from("ruoli").insert(
          ruoli.map((tipo_ruolo) => ({ persona_id: personaId!, tipo_ruolo }))
        );
        if (error) throw error;
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["persone"] });
      queryClient.invalidateQueries({ queryKey: ["ruoli"] });
      toast.success(variables.persona.id ? "Persona aggiornata" : "Persona creata");
      setDialogOpen(false);
      setEditingPersona(null);
    },
    onError: (error) => {
      toast.error("Errore: " + error.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("persone").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["persone"] });
      queryClient.invalidateQueries({ queryKey: ["ruoli"] });
      toast.success("Persona eliminata");
      setDeletingPersona(null);
    },
    onError: (error) => {
      toast.error("Errore: " + error.message);
    },
  });

  const filtered = persone.filter((p) => {
    if (personeIdsPerCorso && !personeIdsPerCorso.has(p.id)) return false;
    if (filtroRuolo !== "tutti" && !(ruoliMap[p.id] || []).includes(filtroRuolo as TipoRuolo)) return false;
    const q = search.toLowerCase();
    return (
      p.nome.toLowerCase().includes(q) ||
      p.cognome.toLowerCase().includes(q) ||
      (p.codice_fiscale?.toLowerCase().includes(q) ?? false) ||
      (p.email?.toLowerCase().includes(q) ?? false)
    );
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Persone</h1>
          <p className="text-muted-foreground mt-1">Gestione anagrafica di atleti, soci, genitori e staff</p>
        </div>
        <Button onClick={() => { setEditingPersona(null); setDialogOpen(true); }}>
          <Plus className="h-4 w-4 mr-2" /> Nuova Persona
        </Button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative max-w-sm flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Cerca per nome, cognome, CF, email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={filtroCorso} onValueChange={setFiltroCorso}>
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder="Filtra per corso" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="tutti">Tutti i corsi</SelectItem>
            {corsi.map((c) => (
              <SelectItem key={c.id} value={c.nome}>{c.nome}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filtroRuolo} onValueChange={setFiltroRuolo}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filtra per ruolo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="tutti">Tutti i ruoli</SelectItem>
            {(Object.keys(RUOLO_COLORS) as TipoRuolo[]).map((r) => (
              <SelectItem key={r} value={r}>{r}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(filtroCorso !== "tutti" || filtroRuolo !== "tutti") && (
          <Button variant="ghost" size="sm" onClick={() => { setFiltroCorso("tutti"); setFiltroRuolo("tutti"); }}>
            Rimuovi filtri
          </Button>
        )}
      </div>

      <div className="rounded-lg border bg-card">
        {isLoading ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground">Caricamento...</div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="p-4 rounded-2xl bg-primary/10 mb-4">
              <Users className="h-10 w-10 text-primary" />
            </div>
            <p className="text-lg font-semibold">Nessuna persona trovata</p>
            <p className="text-sm text-muted-foreground mt-1">
              {search ? "Prova a modificare la ricerca" : "Clicca \"Nuova Persona\" per aggiungerne una"}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cognome</TableHead>
                <TableHead>Nome</TableHead>
                <TableHead>Ruoli</TableHead>
                <TableHead>Corsi</TableHead>
                <TableHead className="hidden md:table-cell">Telefono</TableHead>
                <TableHead className="text-center">Cert. Medico</TableHead>
                <TableHead className="text-center">Abbonamento</TableHead>
                <TableHead className="w-[100px]">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((persona) => {
                const certScadenza = persona.certificato_medico_scadenza;
                const certValid = certScadenza ? new Date(certScadenza) >= new Date() : null;
                const abbInfo = abbonamentiMap[persona.id];

                return (
                  <TableRow key={persona.id} className="cursor-pointer" onClick={() => setDetailPersona(persona)}>
                    <TableCell className="font-medium">{persona.cognome}</TableCell>
                    <TableCell>{persona.nome}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(ruoliMap[persona.id] || []).map((ruolo) => (
                          <Badge key={ruolo} variant="outline" className={RUOLO_COLORS[ruolo]}>
                            {ruolo}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(istruttoreCorsiMap[persona.id] || []).map((corso) => (
                          <Badge key={corso} variant="outline" className={cn("text-xs", getCorsoColorClass(corso))}>
                            {corso}
                          </Badge>
                        ))}
                        {(atletaCorsiMap[persona.id] || []).map((corso) => (
                          <Badge key={corso} variant="outline" className={cn("text-xs", getCorsoColorClass(corso))}>
                            {corso}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">{persona.telefono || "—"}</TableCell>
                    <TableCell className="text-center">
                      {certValid === null ? (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Minus className="h-4 w-4" /> N/D</span>
                      ) : certValid ? (
                        <span className="inline-flex items-center gap-1 text-xs text-green-600"><ShieldCheck className="h-4 w-4" /> Valido</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-red-600"><ShieldX className="h-4 w-4" /> Scaduto</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {!abbInfo ? (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Minus className="h-4 w-4" /> N/D</span>
                      ) : abbInfo.attivo ? (
                        <span className="inline-flex items-center gap-1 text-xs text-green-600"><CheckCircle2 className="h-4 w-4" /> Attivo</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-red-600"><XCircle className="h-4 w-4" /> Scaduto</span>
                      )}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => { setEditingPersona(persona); setDialogOpen(true); }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeletingPersona(persona)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <PersonaDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        persona={editingPersona}
        personaRuoli={editingPersona ? (ruoliMap[editingPersona.id] || []) : []}
        onSave={(persona, ruoli) => upsertMutation.mutate({ persona, ruoli })}
        isSaving={upsertMutation.isPending}
      />

      <DeletePersonaDialog
        persona={deletingPersona}
        onOpenChange={(open) => { if (!open) setDeletingPersona(null); }}
        onConfirm={() => { if (deletingPersona) deleteMutation.mutate(deletingPersona.id); }}
        isDeleting={deleteMutation.isPending}
      />

      <PersonaDetailSheet
        persona={detailPersona}
        ruoli={detailPersona ? (ruoliMap[detailPersona.id] || []) : []}
        onClose={() => setDetailPersona(null)}
      />
    </motion.div>
  );
}
